import * as PIXI from "pixi.js";
import {Ease, ITween} from "../core/Animatable";
import {Container} from "../core/Container";
import {Callback} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import {IBookData} from "../data/BooksData";
import {ColorOverlayFilter} from "../filters/ColorOverlayFilter";
import {GlowFilter} from "../filters/GlowFilter";
import {VoiceOverManager} from "../managers/VoiceOverManager";
import {BurstSparkleEffect} from "./BurstSparkleEffect";
import {ShelfButton} from "./ShelfButton";

export class BookButton extends ShelfButton {

    get wasPulsed(): boolean {
        return this._wasPulsed;
    }

    private static readonly _disabledFilters: Array<PIXI.Filter<any>> = [
        new ColorOverlayFilter(0x000000, 0.4),
    ];

    public readonly bookData: IBookData;
    public isUnlocked = false;
    public isNew = false;

    private _pulseTween?: ITween;
    private _wasPulsed = false;
    private _highlighted = false;

    constructor(bookData: IBookData) {
        super();
        this.bookData = bookData;
    }

    public initHoverVO() {
        if (!PIXI.utils.isMobile.any) {
            this.onOver.add(() => {
                if (this.bookData.authorVO) {
                    VoiceOverManager.instance.playVO(this.bookData.authorVO, {priority: -1});
                }
            });
        }
    }

    public updateState(): void {
        super.updateState();
        this.filters = this.enabled ? null : BookButton._disabledFilters;
    }

    public pulse() {
        if (this._pulseTween) {
            return;
        }
        const s = 1.06;
        this._pulseTween = this.tweenTo<BookButton>(0.4, Ease.sineInOut, {
            scaleXY: this.scaleXY * s,
            y: this.y + this.height * (1 - s),
            x: this.x + this.width * (1 - s) / 2,
            repeat: -1,
            yoyo: true,
        });
        this._wasPulsed = true;
    }

    public stopPulse() {
        if (this._pulseTween) {
            this._pulseTween.progress(0);
            this._pulseTween.kill();
            this._pulseTween = undefined;
        }
    }

    public burstEffect() {
        BurstSparkleEffect.play(this, this.visuals.width / 2, this.visuals.height / 2, 1);
    }

    public flashEffect(parent: Container, onOpaque?: Callback, onTransparent?: Callback) {
        const glow = parent.addChild(new Sprite());
        glow.setLayoutTexture(this.sprite.texture, this.anchor.x, this.anchor.y);
        glow.width = this.width;
        glow.height = this.height;
        glow.x = this.x;
        glow.y = this.y;
        glow.filters = [
            new ColorOverlayFilter(0xffffff),
            new GlowFilter(),
        ];
        for (const filter of glow.filters) {
            filter.padding = 20;
        }
        glow.alphaFrom(0, 0.2, () => {
            if (onOpaque) {
                onOpaque();
            }
            glow.alphaTo(0, 0.2, () => {
                glow.destroy();
                if (onTransparent) {
                    onTransparent();
                }
            });
        });
    }

    public highlight() {
        if (this._highlighted) {
            return;
        }
        this._highlighted = true;
        const glow = this.visuals.addChildAt(new Sprite(), 0);
        glow.setLayoutTexture(this.sprite.texture, this.anchor.x, this.anchor.y);
        glow.width = this.sprite.width;
        glow.height = this.sprite.height;
        glow.x = this.sprite.x;
        glow.y = this.sprite.y;
        glow.filters = [new GlowFilter(15, 6)];
        for (const filter of glow.filters) {
            filter.padding = 30;
        }
        glow.alphaFrom(0, 0.8, {
            repeat: -1,
            yoyo: true,
        });
    }
}
